/**
 * MATERIAL REALITY — what is actually on site, and on whose word.
 *
 * The question this answers is the one a superintendent asks before sending a
 * crew: IS THE MATERIAL THERE? The answer is never a single date, because the
 * records that carry a date do not carry equal weight. A supplier's expected
 * date and a signed receipt are both "a date"; only one of them is an event.
 *
 * So every row states three things that a file-manager-shaped screen would
 * have collapsed into one:
 *
 *     THE DATE            what day
 *     ON WHOSE AUTHORITY  supplier expectation ... field verified
 *     WHETHER IT IS SETTLED  established / records disagree / no date at all
 *
 * WHAT THIS BOARD WILL NOT DO. It will not choose between two records that
 * disagree — the resolver deliberately picks no winner at equal authority, and
 * a UI that quietly showed one of them would be inventing the decision the
 * backend refused to make. A CONFLICT shows both, side by side, and says in
 * words that OMNΛS has not chosen.
 *
 * It renders ONE tree at every width: the layout changes with CSS, the content
 * does not. Two trees behind a media query would put two copies of every id
 * and every action in the DOM.
 */
import {
  AlertTriangle,
  Boxes,
  ClipboardCheck,
  HelpCircle,
  Scale,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  SectionError,
  SectionLoading,
} from '../../components/workflow/WorkflowStates';
import {
  authoritySummary,
  materialStatusDisplay,
} from './materialPresentation';

const TONE_TEXT = {
  settled: 'text-emerald-400',
  conflict: 'text-amber-400',
  unknown: 'text-muted-foreground',
};

/**
 * Status as a WORD plus a non-colour mark. A gang box in daylight is not a
 * place where hue is a reliable channel.
 */
export const StatusChip = ({ display, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1 whitespace-nowrap ${
      TONE_TEXT[display.tone] || TONE_TEXT.unknown
    }`}
  >
    <span aria-hidden="true" className="font-mono text-[0.7rem] leading-none">
      {display.mark}
    </span>
    <span className="text-[0.7rem] font-medium uppercase tracking-wide">
      {display.label}
    </span>
  </span>
);

/**
 * The date, and the record that carries it — never one without the other.
 *
 * An EXPECTATION is labelled as one. The single most consequential mistake
 * this surface could make is letting a plan read as an event.
 */
export const AuthorityLine = ({ material }) => {
  const { authorityDisplay: authority, deliveryDateLabel, status } = material;
  if (status === 'UNKNOWN') {
    return (
      <span className="text-[0.7rem] text-muted-foreground">
        No record states a delivery date.
      </span>
    );
  }
  if (status === 'CONFLICT') {
    return (
      <span className="text-[0.7rem] text-amber-300">
        Two records of equal weight give different dates.
      </span>
    );
  }
  return (
    <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
      <span className="font-mono text-[0.8rem] text-foreground">
        {deliveryDateLabel}
      </span>
      <span className="text-[0.7rem] text-muted-foreground">
        on <span className="text-foreground">{authority.label}</span>
      </span>
      {authority.kind === 'EXPECTATION' && (
        <span
          data-testid={`expectation-${material.id}`}
          className="rounded-full border border-border px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wide text-muted-foreground"
        >
          Expected, not arrived
        </span>
      )}
      {authority.kind === 'PROPOSAL' && (
        <span
          data-testid={`proposal-${material.id}`}
          className="rounded-full border border-amber-500/40 px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wide text-amber-300"
        >
          Worked out, not recorded
        </span>
      )}
    </span>
  );
};

/** Both sides of a disagreement, with no winner marked. */
export const ConflictDetail = ({ material }) => {
  if (material.status !== 'CONFLICT' || material.conflicts.length === 0)
    return null;
  return (
    <div
      data-testid={`conflict-${material.id}`}
      className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2"
    >
      <p className="flex items-center gap-1.5 text-[0.7rem] font-medium text-foreground">
        <Scale className="h-3.5 w-3.5 text-amber-400" aria-hidden="true" />
        Records disagree
      </p>
      <ul className="mt-1 space-y-1">
        {material.conflicts.map((c, index) => (
          <li
            key={`${c.sourceType || 'record'}-${c.sourceId || index}`}
            className="text-[0.7rem] text-muted-foreground"
          >
            <span className="font-mono text-foreground">
              {c.deliveryDateLabel}
            </span>{' '}
            — {c.authorityDisplay.label}
            {c.sourceType && (
              <span className="text-muted-foreground/80">
                {' '}
                ({c.sourceType})
              </span>
            )}
          </li>
        ))}
      </ul>
      {/*
        Said in words, not implied by the absence of a highlight. The resolver
        refuses to choose; this screen refuses too, and says so.
      */}
      <p className="mt-1 text-[0.7rem] text-foreground">
        OMNΛS has not chosen between them. Nothing should be planned against
        either date until a stronger record settles it.
      </p>
    </div>
  );
};

/** Why there is no date. The backend's own sentence, never ours. */
export const UnknownDetail = ({ material }) => {
  if (material.status !== 'UNKNOWN') return null;
  return (
    <div
      data-testid={`unknown-${material.id}`}
      className="mt-2 rounded-md border border-border/60 bg-muted/20 p-2"
    >
      <p className="flex items-center gap-1.5 text-[0.7rem] font-medium text-foreground">
        <HelpCircle
          className="h-3.5 w-3.5 text-muted-foreground"
          aria-hidden="true"
        />
        No delivery date established
      </p>
      <p className="mt-1 text-[0.7rem] text-muted-foreground">
        {material.unknownReason ||
          'No evidence supports a delivery date. Nothing has been inferred from lead time or an order date.'}
      </p>
    </div>
  );
};

export const MaterialRow = ({ material, onRecord, canRecord = true }) => (
  <li
    data-testid={`material-row-${material.id}`}
    data-status={material.status || 'NOT_STATED'}
    className="px-3 py-3"
  >
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm font-medium text-foreground">
          {material.nameLabel}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <StatusChip
            display={material.statusDisplay}
            testId={`material-status-${material.id}`}
          />
          <AuthorityLine material={material} />
        </div>
      </div>
      {canRecord && (
        <Button
          variant="outline"
          size="sm"
          className="min-h-[2.25rem] shrink-0"
          onClick={() => onRecord?.(material)}
          data-testid={`record-material-${material.id}`}
        >
          <ClipboardCheck className="h-3.5 w-3.5" />
          Record what happened
        </Button>
      )}
    </div>

    <ConflictDetail material={material} />
    <UnknownDetail material={material} />

    {material.warnings.length > 0 && (
      <ul
        data-testid={`material-warnings-${material.id}`}
        className="mt-2 space-y-0.5"
      >
        {material.warnings.map((w) => (
          <li
            key={w}
            className="flex gap-1.5 text-[0.7rem] text-muted-foreground"
          >
            <AlertTriangle
              className="mt-0.5 h-3 w-3 shrink-0 text-amber-400"
              aria-hidden="true"
            />
            <span>{w}</span>
          </li>
        ))}
      </ul>
    )}
  </li>
);

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'CONFLICT', label: 'Records disagree' },
  { value: 'UNKNOWN', label: 'No date' },
  { value: 'RESOLVED', label: 'Established' },
];

export const MaterialRealityBoard = ({
  materials,
  counts,
  unreadable = [],
  contractError = null,
  loadError = null,
  loading = false,
  onRecord,
  onRetry,
  canRecord = true,
  query = '',
  onQuery,
  statusFilter = 'all',
  onStatusFilter,
  truncated = false,
}) => {
  if (loading) return <SectionLoading label="Reading the delivery record…" />;
  if (loadError) {
    return (
      <SectionError
        error={loadError}
        onRetry={onRetry}
        label="The delivery record could not be read"
      />
    );
  }
  if (contractError) {
    /*
      The envelope could not be read. NOT an empty project: reporting it as
      "no materials" would be a claim about the job.
    */
    return (
      <div
        data-testid="material-contract-error"
        className="px-4 py-8 text-center"
      >
        <p className="text-sm font-medium text-foreground">
          The delivery record could not be read
        </p>
        <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">
          {contractError}
        </p>
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={onRetry}
          >
            Retry
          </Button>
        )}
      </div>
    );
  }

  const summary = authoritySummary(counts);

  return (
    <div data-testid="material-reality-board" className="flex flex-col gap-3">
      {/*
        One sentence, and it is a count rather than a score. "6 of 15 have no
        date you can plan against" is actionable; "60% healthy" is not.
      */}
      <div className="rounded-md border border-border/60 bg-muted/20 p-3">
        <p
          className="flex items-start gap-2 text-sm text-foreground"
          data-testid="material-summary"
        >
          <Boxes
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <span>{summary.headline}</span>
        </p>
        {summary.parts.length > 0 && (
          <p className="mt-1 pl-6 text-[0.7rem] text-muted-foreground">
            {summary.parts.join(' · ')}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          type="search"
          value={query}
          onChange={(e) => onQuery?.(e.target.value)}
          placeholder="Search materials…"
          aria-label="Search materials by name or evidence"
          data-testid="material-search"
          className="bg-muted/50 sm:max-w-xs"
        />
        {/*
          Plain buttons rather than a select: on a phone this is one tap, and
          it keeps the whole board renderable in a unit test.
        */}
        <div
          className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1"
          role="group"
          aria-label="Filter materials by whether their date is settled"
        >
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              data-testid={`material-filter-${f.value}`}
              aria-pressed={statusFilter === f.value}
              onClick={() => onStatusFilter?.(f.value)}
              className={`min-h-[2.25rem] shrink-0 rounded-full px-3 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                statusFilter === f.value
                  ? 'bg-primary/20 font-semibold text-foreground'
                  : 'bg-muted/50 text-muted-foreground'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {unreadable.length > 0 && (
        <p
          data-testid="material-unreadable"
          className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-muted-foreground"
        >
          <span className="font-medium text-foreground">
            {unreadable.length} record{unreadable.length === 1 ? '' : 's'} could
            not be read.
          </span>{' '}
          They are not shown and are not counted as materials. This is a payload
          problem, not an empty project.
        </p>
      )}

      {truncated && (
        <p
          className="text-xs text-muted-foreground"
          data-testid="material-truncated"
        >
          The backend returned more materials than it lists in one response.
          This is not all of them.
        </p>
      )}

      {materials.length === 0 ? (
        <div
          data-testid="material-list-empty"
          className="px-4 py-8 text-center text-muted-foreground"
        >
          <Boxes
            className="mx-auto mb-3 h-8 w-8 opacity-50"
            aria-hidden="true"
          />
          <p className="text-sm font-medium text-foreground">
            No materials match
          </p>
          <p className="mt-1 text-xs">Adjust the search or the filter.</p>
        </div>
      ) : (
        <ul
          data-testid="material-list"
          className="divide-y divide-border rounded-md border border-border/60"
        >
          {materials.map((m) => (
            <MaterialRow
              key={m.id}
              material={m}
              onRecord={onRecord}
              canRecord={canRecord}
            />
          ))}
        </ul>
      )}

      <span className="sr-only" role="status" aria-live="polite">
        {materials.length} material{materials.length === 1 ? '' : 's'} listed
      </span>
    </div>
  );
};

export default MaterialRealityBoard;
